import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Provider } from 'react-redux'
import { Toaster } from 'react-hot-toast'
import { useEffect, useState } from 'react'

import '@/i18n/config' // Import i18n configuration
// Template config integration v2.0 - 2025-10-20
import { store } from '@/store'
import { ImageProvider } from '@/contexts/ImageContext'
import { ErrorBoundary, ViewerErrorFallback } from '@/components/common/ErrorBoundary'
import Layout from '@/components/layout/Layout'
import Dashboard from '@/features/dashboard/Dashboard'
import Login from '@/features/auth/Login'
import PatientManagement from '@/features/pathology/PatientManagement'
import PatientReception from '@/features/pathology/PatientReception'
import PatientHistory from '@/features/pathology/PatientHistory'
import WorkflowTracking from '@/features/pathology/WorkflowTracking'
import UnifiedWorklist from '@/features/workflow/UnifiedWorklist'
import TechnicianWorklist from '@/features/workflow/TechnicianWorklist'
import PathologistWorklist from '@/features/workflow/PathologistWorklist'
import ApprovalWorklist from '@/features/workflow/ApprovalWorklist'
import ResultsWorklist from '@/features/workflow/ResultsWorklist'
import ReportManagement from '@/features/pathology/ReportManagement'
import ReportForm from '@/features/pathology/ReportForm'
import CytologyReportTemplate from '@/features/pathology/CytologyReportTemplate'
import CytologyReportEditor from '@/features/pathology/CytologyReportEditor'
import CytologyReportViewer from '@/features/pathology/CytologyReportViewer'
import CytologyReportImageGallery from '@/features/pathology/CytologyReportImageGallery'
import CytologyTemplateEditor from '@/features/pathology/CytologyTemplateEditor'
import CytologyFormBuilder from '@/features/pathology/CytologyFormBuilder'
import CytologyReportTest from '@/features/pathology/CytologyReportTest'
import ImprovedSVSViewer from '@/features/imaging/ImprovedSVSViewer'
import MultiViewPage from '@/features/imaging/MultiViewPage'
import ImageLibrary from '@/features/imaging/ImageLibrary'
import SimpleImageUpload from '@/features/imaging/SimpleImageUpload'
import DiagnosisTemplates from '@/features/diagnosis/DiagnosisTemplates'
import DeviceStatus from '@/features/devices/DeviceStatus'
import AnalyticsReports from '@/features/analytics/AnalyticsReports'
import UserManagement from '@/features/admin/UserManagement'
import DepartmentManagement from '@/features/admin/DepartmentManagement'
import PermissionsManagement from '@/features/admin/PermissionsManagement'
import SystemSettings from '@/features/admin/SystemSettings'
import OrganizationList from '@/features/admin/OrganizationList'
import RoleManagement from '@/features/admin/RoleManagement'
import IntegrationManagement from '@/features/admin/IntegrationManagement'
import ScannerManagement from '@/features/devices/ScannerManagement'
import MaintenanceRecords from '@/features/devices/MaintenanceRecords'
import CalibrationManagement from '@/features/devices/CalibrationManagement'
import DeviceAlerts from '@/features/devices/DeviceAlerts'
import BackupSettings from '@/features/backup/BackupSettings'
import ScannerPage from './features/scanner/ScannerPage'
import PatientFormTemplateList from '@/features/pathology/PatientFormTemplateList'
import PatientFormTemplateDetail from '@/features/pathology/PatientFormTemplateDetail'
import PatientFormBuilder from '@/features/pathology/PatientFormBuilder'

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const token = localStorage.getItem('authToken')
  return token ? <>{children}</> : <Navigate to="/login" replace />
}

// Auth Guard component for login redirect
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const token = localStorage.getItem('authToken')
  return token ? <Navigate to="/dashboard" replace /> : <>{children}</>
}

function App() {
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Simulate checking authentication status
    const checkAuth = () => {
      // In a real app, you might validate the token with the server
      setIsLoading(false)
    }

    checkAuth()
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading PIS System...</p>
        </div>
      </div>
    )
  }

  return (
    <Provider store={store}>
      <ImageProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10B981',
                },
              },
              error: {
                style: {
                  background: '#EF4444',
                },
              },
            }}
          />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              <AuthGuard>
                <Login />
              </AuthGuard>
            } />

            {/* Full-screen viewer (outside of layout) */}
            {/* Protected imaging viewer - full screen */}
            <Route path="/imaging/viewer/:imageId" element={
              <ProtectedRoute>
                <ErrorBoundary fallback={<ViewerErrorFallback />}>
                  <ImprovedSVSViewer />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Multi-view page - full screen */}
            <Route path="/imaging/multi-view/:imageId" element={
              <ProtectedRoute>
                <ErrorBoundary fallback={<ViewerErrorFallback />}>
                  <MultiViewPage />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              {/* Dashboard */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={
                <ErrorBoundary>
                  <Dashboard />
                </ErrorBoundary>
              } />

              {/* Workflow routes */}
              <Route path="workflow/tracking" element={
                <ErrorBoundary>
                  <WorkflowTracking />
                </ErrorBoundary>
              } />
              <Route path="workflow/worklist" element={
                <ErrorBoundary>
                  <UnifiedWorklist />
                </ErrorBoundary>
              } />
              <Route path="workflow/technician" element={
                <ErrorBoundary>
                  <TechnicianWorklist />
                </ErrorBoundary>
              } />
              <Route path="workflow/pathologist" element={
                <ErrorBoundary>
                  <PathologistWorklist />
                </ErrorBoundary>
              } />
              <Route path="workflow/approval" element={
                <ErrorBoundary>
                  <ApprovalWorklist />
                </ErrorBoundary>
              } />
              <Route path="workflow/results" element={
                <ErrorBoundary>
                  <ResultsWorklist />
                </ErrorBoundary>
              } />

              {/* Pathology routes */}
              <Route path="pathology/patients" element={
                <ErrorBoundary>
                  <PatientReception />
                </ErrorBoundary>
              } />
              <Route path="pathology/patients/manage" element={
                <ErrorBoundary>
                  <PatientManagement />
                </ErrorBoundary>
              } />
              <Route path="pathology/patients/:id/history" element={
                <ErrorBoundary>
                  <PatientHistory />
                </ErrorBoundary>
              } />
              <Route path="pathology/patients/form-builder" element={
                <ErrorBoundary>
                  <PatientFormTemplateList />
                </ErrorBoundary>
              } />
              <Route path="pathology/patients/form-builder/editor/:id" element={
                <ErrorBoundary>
                  <PatientFormBuilder />
                </ErrorBoundary>
              } />
              <Route path="pathology/patients/form-builder/:id" element={
                <ErrorBoundary>
                  <PatientFormTemplateDetail />
                </ErrorBoundary>
              } />
              <Route path="pathology/slides" element={<Navigate to="/imaging/library" replace />} /> {/* Redirect to imaging */}
              <Route path="pathology/reports" element={
                <ErrorBoundary>
                  <ReportManagement />
                </ErrorBoundary>
              } />
              <Route path="pathology/reports/create" element={<ReportForm />} />
              <Route path="pathology/reports/:id/edit" element={<ReportForm />} />
              <Route path="pathology/reports/cytology" element={<CytologyReportTemplate />} />
              <Route path="pathology/reports/cytology/new" element={<CytologyReportEditor />} />
              <Route path="pathology/reports/cytology/:imageId" element={<CytologyReportEditor />} />
              <Route path="pathology/reports/cytology/template/edit" element={<CytologyTemplateEditor />} />
              <Route path="pathology/reports/cytology/form-builder" element={<CytologyFormBuilder />} />
              <Route path="pathology/reports/cytology/test" element={<CytologyReportTest />} />
              <Route path="pathology/cytology-reports/:id" element={<CytologyReportViewer />} />
              <Route path="pathology/cytology-reports/:id/images" element={<CytologyReportImageGallery />} />

              {/* Imaging routes - PIS SYSTEM */}
              <Route path="imaging" element={
                <ErrorBoundary>
                  <ImageLibrary />
                </ErrorBoundary>
              } />
              <Route path="imaging/library" element={
                <ErrorBoundary>
                  <ImageLibrary />
                </ErrorBoundary>
              } />
              <Route path="imaging/upload" element={
                <ErrorBoundary>
                  <SimpleImageUpload />
                </ErrorBoundary>
              } />
              {/* <Route path="imaging/jobs" element={<ProcessingJobs />} /> REMOVED - not needed */}

              {/* Devices routes */}
              <Route path="devices/status" element={<DeviceStatus />} />
              <Route path="devices/scanners" element={<ScannerManagement />} />
              <Route path="devices/maintenance" element={<MaintenanceRecords />} />
              <Route path="devices/calibration" element={<CalibrationManagement />} />
              <Route path="devices/alerts" element={<DeviceAlerts />} />

              {/* Scanner routes - HDScanner Integration */}
              <Route path="scanner" element={
                <ErrorBoundary>
                  <ScannerPage />
                </ErrorBoundary>
              } />

              {/* Analytics routes */}
              <Route path="analytics/reports" element={<AnalyticsReports />} />
              <Route path="analytics/templates" element={<DiagnosisTemplates />} />

              {/* Administration routes */}
              <Route path="admin/organizations" element={<OrganizationList />} />
              <Route path="admin/users" element={<UserManagement />} />
              <Route path="admin/roles" element={<RoleManagement />} />
              <Route path="admin/departments" element={<DepartmentManagement />} />
              <Route path="admin/permissions" element={<PermissionsManagement />} />
              <Route path="admin/integration" element={<IntegrationManagement />} />
              <Route path="admin/settings" element={<SystemSettings />} />
              <Route path="admin/backup" element={<BackupSettings />} />

              {/* Legacy routes for backward compatibility */}
              <Route path="viewer/:imageId" element={<Navigate to="/imaging/viewer/:imageId" replace />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </div>
        </Router>
      </ImageProvider>
    </Provider>
  )
}

export default App